import { CloudAccountType } from '@internal/backstage-plugin-catalog-devops-common';
import styles from './Sidebar.module.css';

export type CloudProvider = 'aws' | 'az' | 'gcp' | 'other';

/**
 * Maps an AccountEntity `spec.type` (CloudAccountType) to the sidebar's
 * provider badge kind. Unknown types render no badge instead of guessing
 * a brand.
 */
export function providerOf(accountType: string | undefined): CloudProvider {
  switch (accountType) {
    case CloudAccountType.AWS:
      return 'aws';
    case CloudAccountType.AZURE:
      return 'az';
    case 'GCP':
      return 'gcp';
    default:
      return 'other';
  }
}

/** Catalog link to an entity page, e.g. /catalog/default/account/foo. */
export function entityHref(
  entity: { metadata: { namespace?: string; name: string } },
  kind: string,
): string {
  return `/catalog/${entity.metadata.namespace ?? 'default'}/${kind.toLowerCase()}/${
    entity.metadata.name
  }`;
}

const PROVIDER_BADGE: Record<Exclude<CloudProvider, 'other'>, { cls: string; label: string }> = {
  aws: { cls: styles.pvAws, label: 'aws' },
  az: { cls: styles.pvAz, label: 'Az' },
  gcp: { cls: styles.pvGcp, label: 'G' },
};

/**
 * Small monospace provider badge ("aws" / "Az" / "G") shown before the
 * account name. In rail mode it is the only visual hint of the provider.
 */
export function ProviderBadge({ provider }: { provider: CloudProvider }) {
  if (provider === 'other') {
    return null;
  }
  const badge = PROVIDER_BADGE[provider];
  return <span className={`${styles.pv} ${badge.cls}`}>{badge.label}</span>;
}

const CLUSTER_TYPE_CHIP: Record<string, { cls: string; label: string }> = {
  EKS: { cls: styles.tchipEks, label: 'EKS' },
  AKS: { cls: styles.tchipAks, label: 'AKS' },
  K8S: { cls: styles.tchipK8s, label: 'K8s' },
  GKE: { cls: styles.tchipGke, label: 'GKE' },
};

/**
 * Cluster-type chip (EKS / AKS / K8s / GKE) shown before the cluster name.
 * Unknown types get a neutral chip with a truncated uppercase label.
 */
export function ClusterTypeChip({ type }: { type: string | undefined }) {
  const normalized = (type ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const known = CLUSTER_TYPE_CHIP[normalized];
  if (known) {
    return <span className={`${styles.tchip} ${known.cls}`}>{known.label}</span>;
  }
  return (
    <span className={`${styles.tchip} ${styles.tchipOther}`}>
      {(type ?? '?').slice(0, 3).toUpperCase()}
    </span>
  );
}
